import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../../components/Modal";
import useApi from "../../../../utils/api";
import { useAccess } from "../../../../utils/hooks/access";
import { useCompanyDateFormat } from "../../../../utils/hooks/company";
import { describeScheduledChange, scheduledChangeProblem } from "../../../Profile/TimeOff/ScheduledChanges";

/**
 * Task list line "Scheduled PTO plan changes that could not be applied": the manager's team's
 * future-dated transfers / assigns the daily job refused on their day (retried daily) or that
 * failed (settings `/time-off-plan/scheduled-changes`). The line opens the list with the reason
 * and a link to the employee's Time off, where the change can be cancelled.
 */
function PtoScheduledChanges() {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { authGet } = useApi();
    const { hasAccess } = useAccess("profile.canAddTeamPlan");
    const [rows, setRows] = useState([]);
    const [listOpen, setListOpen] = useState(false);

    const fetch = useCallback(
        async (controller) => {
            try {
                const response = await authGet("/time-off-plan/scheduled-changes", { signal: controller?.signal });
                setRows(Array.isArray(response) ? response : []);
            } catch (error) {
                // the task list stays usable without this line
                setRows([]);
            }
        },
        [authGet],
    );

    useEffect(() => {
        if (!hasAccess) return;
        const controller = new AbortController();
        fetch(controller);
        return () => controller.abort();
    }, [hasAccess, fetch]);

    if (!hasAccess) return null;

    const count = rows.length;
    const name = (row) => [row.employee?.firstName, row.employee?.lastName].filter(Boolean).join(" ") || row.employee?.employeeNumber;

    return (
        <>
            <div className="d-flex border-bottom align-items-center py-2">
                <div>
                    <i className="fa-solid fa-calendar-xmark text-lg mr-3 text-danger" />
                </div>
                <div className="d-flex flex-column">
                    <span className="mb-1">{t("scheduled-pto-changes-not-applied")}</span>
                    <span
                        className={classNames("text-sm font-weight-bold text-danger", count > 0 && "cursor-pointer")}
                        onClick={count > 0 ? () => setListOpen(true) : undefined}
                    >
                        {t("review-scheduled-pto-changes", { count })}
                        <i className="fa-solid fa-greater-than text-xs ml-2" />
                    </span>
                </div>
            </div>

            <Modal
                isOpen={listOpen}
                toggle={() => setListOpen(false)}
                size="lg"
                scrollable
                title={
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <span>{t("scheduled-pto-changes-not-applied")}</span>
                        <FontAwesomeIcon className="cursor-pointer" icon={faXmark} onClick={() => setListOpen(false)} />
                    </div>
                }
            >
                {count === 0 ? (
                    <p className="text-muted text-sm mb-0">{t("no-scheduled-pto-changes-not-applied")}</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th>{t("pending-adjustment-col-employee")}</th>
                                    <th>{t("scheduled-change-col-change")}</th>
                                    <th>{t("scheduled-change-col-reason")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id}>
                                        <td style={{ whiteSpace: "normal" }}>
                                            <Link to={`/people/${row.employee?.id}/time-off`}>{name(row)}</Link>
                                            {row.employee?.employeeNumber && (
                                                <small className="d-block text-muted">{row.employee.employeeNumber}</small>
                                            )}
                                        </td>
                                        <td style={{ whiteSpace: "normal" }}>{describeScheduledChange(row, t, dateFormat)}</td>
                                        <td className="text-danger" style={{ whiteSpace: "normal" }}>{scheduledChangeProblem(row, t, dateFormat)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>
        </>
    );
}

export default PtoScheduledChanges;
